'use client';

import { useEffect, useState, useRef } from 'react';

import { FormInfo } from '../types';
import JSONSchemaBridge from 'uniforms-bridge-json-schema';
import AutoForm from './styledUniforms/src/AutoForm';
import fetcher, { Response } from '@/lib/utils/fetcherBasic';
import TitleH1 from '../common/TitleH1';
import SubmitField from './styledUniforms/src/SubmitField';
import { createValidator } from './formValidator';
import createSchema from './schemaBuilder';
import Paragraph from '../common/Paragraph';
import { usePathname, useSearchParams } from 'next/navigation';

interface Props {
  formInfo: FormInfo;
}

export function DirectusForm({ formInfo }: Props) {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [succeedMessage, setSucceedMessage] = useState<string>('');
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [formBridge, setFormBridge] = useState<JSONSchemaBridge | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const formRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const fullUrl = `${window.location.origin}${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      setCurrentUrl(fullUrl);
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    if (currentUrl) {
      formInfo.fields.push({
        type: 'hidden',
        name: 'form-url',
        value: currentUrl,
        label: 'form-url',
        sort: 1,
        selectValues: [],
        required: false,
      });
    }

    let { schema, fieldLabels } = createSchema(formInfo);
    let schemaValidator = createValidator(schema, fieldLabels);

    setFormBridge(
      new JSONSchemaBridge({
        schema,
        validator: schemaValidator,
      }),
    );
  }, [currentUrl, formInfo, setFormBridge]);

  const submitForm = async (data: FormProperties) => {
    setInProgress(true);
    const formData = new FormData();

    let hiddenFields = formInfo.fields.filter((field) => field.type === 'hidden').map((val) => val.name);
    // hidden fields case
    formInfo.fields.forEach(({ name, type, value }) => {
      if (type === 'hidden' && value) {
        formData.append(name, value);
      }
    });

    Object.keys(data).forEach((key) => {
      // do not edit hidden fields
      if (!hiddenFields.includes(key)) {
        formData.append(key, data[key]);
      }
    });

    formData.append('form', formInfo.formId);

    const response = await fetcher<string>(`/api/form`, {
      method: 'POST',
      body: formData,
    });

    setInProgress(false);

    if ((response as Response).message === 'error') {
      setErrorMessage(formInfo.submitErrorMessage);
    } else {
      if (formRef.current) {
        // @ts-ignore
        formRef.current.reset();
      }
      setSucceedMessage(formInfo.submitSuccessMessage);
    }
  };

  useEffect(() => {
    if (errorMessage) {
      setTimeout(() => setErrorMessage(''), 3000);
    }
    if (succeedMessage) {
      setTimeout(() => setSucceedMessage(''), 3000);
    }
  }, [errorMessage, succeedMessage]);

  const CustomSubmitButton = (props: any) => (
    <SubmitField {...props} value={formInfo.submit_label ?? 'Valider'} disabled={inProgress} />
  );

  return (
    <div className="flex justify-center p-0 lg:min-h-[700px] lg:p-20 lg:bg-brush-ellipse-left lg:bg-brush-ellipse-right">
      <div className="z-10 w-full lg:w-[648px]">
        <TitleH1 styles={`grow ${formInfo.description ? '' : 'mb-7'}`} size="medium" color="black">
          {formInfo.block_title ?? ''}
        </TitleH1>
        {formInfo.description && (
          <Paragraph
            styles="text-gray-400
      text-sm mb-7"
          >
            {formInfo.description}
          </Paragraph>
        )}

        {formBridge && (
          <AutoForm
            schema={formBridge}
            onSubmit={submitForm}
            submitField={CustomSubmitButton}
            id={formInfo.formId}
            ref={formRef}
          />
        )}
        {errorMessage && (
          <div role="alert" className="alert alert-error my-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 shrink-0 stroke-current"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{errorMessage}</span>
          </div>
        )}
        {succeedMessage && (
          <div role="alert" className="alert alert-success my-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 shrink-0 stroke-current"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{succeedMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
}
